import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Sun, Moon, Sunset } from 'lucide-react';
import { DashboardProvider, DashboardPeriod, DashboardScope } from './DashboardContext';
import { Roles } from '../../components/roles';
import KPISection from './components/KPISection';
import PendingActionsCard from '../../components/pendingActions/PendingActionsCard';
import HolidayCard from './components/HolidayCard';
import FreeTierShowcase from './components/FreeTierShowcase';
import Select from '../../components/ui/Select';
import { type CurrencyOption } from './components/FilterControls';
import { useDashboardData } from './hooks/useDashboardData';
import { GetCompanyRates } from '../../api/General/currency';
import { PageLayout } from '../../components/ui/PageLayout';
import { useMySubscription } from '../../hooks/useMySubscription';

// =============================================================================
// Types
// =============================================================================

type Props = Readonly<{
  role?: string;
  companyName?: string;
  firstName?: string;
  rtl?: boolean;
  isManager?: boolean;
  baseCurrencyCode?: string;
  baseCurrencySymbol?: string;
}>;

type DashboardContentProps = {
  firstName?: string;
  isClockingOnly: boolean;
  isHr: boolean;
  scope: DashboardScope;
  period: DashboardPeriod;
  currency: string;
  currencyOptions: CurrencyOption[];
  onPeriodChange: (value: DashboardPeriod) => void;
  onCurrencyChange: (value: string) => void;
};

// =============================================================================
// Inner Component: DashboardContent
// Must be inside DashboardProvider to use useDashboardData hook
// =============================================================================

const DashboardContent: React.FC<DashboardContentProps> = ({
  firstName,
  isClockingOnly,
  isHr,
  scope,
  period,
  currency,
  currencyOptions,
  onPeriodChange,
  onCurrencyChange,
}) => {
  const { t } = useTranslation();
  const { loading, error, primaryKPIs, secondaryKPIs, holidays } = useDashboardData();

  // Time-based greeting with lucide icon
  const { greetingText, GreetingIcon } = useMemo(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      return { greetingText: t('Good morning'), GreetingIcon: Sun };
    }
    if (hour >= 12 && hour < 18) {
      return { greetingText: t('Good afternoon'), GreetingIcon: Sun };
    }
    if (hour >= 18 && hour < 22) {
      return { greetingText: t('Good evening'), GreetingIcon: Sunset };
    }
    return { greetingText: t('Good night'), GreetingIcon: Moon };
  }, [t]);

  // Filter KPIs for clocking-only users (personal scope - only attendance and payroll)
  const filteredPrimaryKPIs = useMemo(() => {
    let kpis = primaryKPIs;
    if (isClockingOnly) {
      // For clocking-only users, show only attendance and payroll primary KPIs
      kpis = kpis.filter(
        (kpi) => kpi.category === 'attendance' || kpi.category === 'payroll'
      );
    }
    if (!isHr) {
      // Non-HR users: hide payroll primary card (cost)
      kpis = kpis.filter((kpi) => kpi.category !== 'payroll');
    }
    return kpis;
  }, [primaryKPIs, isClockingOnly, isHr]);

  const filteredSecondaryKPIs = useMemo(() => {
    if (isClockingOnly) {
      return {
        attendance: secondaryKPIs.attendance,
        // Non-HR: only show claims card from payroll; HR: show all payroll
        payroll: !isHr
          ? secondaryKPIs.payroll.filter((kpi) => kpi.id === 'pay-claims')
          : secondaryKPIs.payroll,
        employees: [],
        projects: [],
      };
    }
    if (!isHr) {
      // Non-HR users: only show claims card from payroll secondary KPIs
      return {
        ...secondaryKPIs,
        payroll: secondaryKPIs.payroll.filter((kpi) => kpi.id === 'pay-claims'),
      };
    }
    return secondaryKPIs;
  }, [secondaryKPIs, isClockingOnly, isHr]);

  const greetingTitle = `${greetingText}${firstName ? `, ${firstName}` : ''}`;

  return (
    <PageLayout
      title={greetingTitle}
      icon={GreetingIcon}
      accentColor="blue"
      maxWidth="1600px"
      headerActions={
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {/* Holiday pills — approvals moved into the actionable Pending Actions card below */}
          {holidays?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {holidays?.map((holiday) => (
                <HolidayCard key={holiday?.id} holiday={holiday} />
              ))}
            </div>
          )}
          {/* Filter controls — compact, label-less by default */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground hidden lg:inline">{t('Timeframe')}</span>
            <div className="w-40">
              <Select
                options={[
                  { value: 'this-month', label: t('This Month') },
                  { value: 'last-month', label: t('Last Month') },
                  { value: 'last-3m', label: t('Last 3 Months') },
                  { value: 'ytd', label: t('Year to Date') },
                ]}
                value={period}
                onChange={(value: string) => onPeriodChange(value as DashboardPeriod)}
                placeholder={t('Select period')}
                id="timeframe-select"
              />
            </div>
            {currencyOptions.length > 1 && (
              <div className="w-24">
                <Select
                  options={currencyOptions}
                  value={currency}
                  onChange={(value: string) => onCurrencyChange?.(value)}
                  placeholder={t('Currency')}
                  id="currency-select"
                />
              </div>
            )}
          </div>
        </div>
      }
    >
      {/* Error state */}
      {error && (
        <div className="bg-error/10 border border-error/20 text-error px-4 py-3 rounded-lg mb-6" data-testid="dashboard-error">
          {t('Failed to load dashboard data. Please try again.')}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center py-12" data-testid="dashboard-loading">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}

      {/* Main content - only show when not loading */}
      {!loading && (
        <div data-testid="dashboard-content">
          {/* Everything waiting on the user — approvals + self-actions, actionable inline */}
          <PendingActionsCard />

          {/* Section title */}
          <div className="mb-4 flex items-center gap-3">
            <h2
              className="text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.08em]"
              data-testid="kpi-section-title"
            >
              {isClockingOnly ? t('Your Snapshot') : t('Key Performance Indicators')}
            </h2>
            <div className="flex-1 h-px bg-border/60" />
          </div>

          {/* KPI Section */}
          <KPISection
            primaryKPIs={filteredPrimaryKPIs}
            secondaryKPIs={filteredSecondaryKPIs}
            scope={scope}
          />
        </div>
      )}
    </PageLayout>
  );
};

// =============================================================================
// Main Component: CompanyDashboard
// =============================================================================

const CompanyDashboard = (props: Props) => {
  const { role, firstName, baseCurrencyCode } = props;
  const { t } = useTranslation();
  const { isFree, data: subscriptionData } = useMySubscription();

  // Fetch company-configured currency options and resolve effective base currency
  const [currencyOptions, setCurrencyOptions] = useState<CurrencyOption[]>([]);
  const [resolvedBase, setResolvedBase] = useState<string>(baseCurrencyCode || '');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await GetCompanyRates();
        if (cancelled) return;
        const rates = resp?.payload ?? [];

        // Derive the base currency code from the API response or Redux
        const apiBase = rates[0]?.baseCurrency?.currencyCode;
        const base = apiBase || baseCurrencyCode || '';
        setResolvedBase(base);

        // Build deduplicated options: base currency first, then foreign currencies
        const seen = new Set<string>();
        const options: CurrencyOption[] = [];
        if (base) {
          seen.add(base);
          options.push({ value: base, label: base });
        }
        for (const rate of rates) {
          const code = rate?.foreignCurrency?.currencyCode;
          if (code && !seen.has(code)) {
            seen.add(code);
            options.push({ value: code, label: code });
          }
        }

        setCurrencyOptions(options);
      } catch {
        // Fallback: show at least the base currency from Redux
        if (!cancelled && baseCurrencyCode) {
          setResolvedBase(baseCurrencyCode);
          setCurrencyOptions([{ value: baseCurrencyCode, label: baseCurrencyCode }]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [baseCurrencyCode]);

  // Currency selection state - validated against available options
  const [currency, setCurrency] = useState<string>(() => {
    try {
      return localStorage.getItem('dash.currency') || '';
    } catch {
      return '';
    }
  });

  // Once options load, validate that the selected currency is still valid
  useEffect(() => {
    if (currencyOptions.length === 0) return;
    const isValid = currencyOptions.some((opt) => opt.value === currency);
    if (!isValid) {
      // Reset to the base currency (first option)
      setCurrency(currencyOptions[0].value);
    }
  }, [currencyOptions, currency]);

  // Persist currency preference to localStorage
  useEffect(() => {
    if (!currency) return;
    try {
      localStorage.setItem('dash.currency', currency);
    } catch {
      // Ignore localStorage errors
    }
  }, [currency]);

  // Period state with localStorage persistence
  const [period, setPeriod] = useState<DashboardPeriod>(() => {
    try {
      return (localStorage.getItem('dash.period') as DashboardPeriod) || 'this-month';
    } catch {
      return 'this-month';
    }
  });

  // Persist period preference to localStorage
  React.useEffect(() => {
    try {
      localStorage.setItem('dash.period', period);
    } catch {
      // Ignore localStorage errors
    }
  }, [period]);

  // Role normalization
  const roleStr = role ?? '';
  const normalizedRole = roleStr.toLowerCase();
  const isAdmin = normalizedRole === 'admin';

  // Case-insensitive role check helper
  const includesCI = (values: string[]) =>
    values.some((v) => (v ?? '').toLowerCase() === normalizedRole);

  // Scope rules:
  // - Chiefs and HR see company (case-insensitive membership), but explicitly exclude Admin
  // - Managers see team
  // - Admin should behave like Clocking (personal scope)
  const canSeeCompanyBlocks = useMemo(() => {
    const isHrOrChief = (includesCI(Roles.HR) && !isAdmin) || includesCI(Roles.CHIEF);
    return isHrOrChief;
  }, [normalizedRole, isAdmin]);

  const isManager = useMemo(() => {
    return includesCI(Roles.MANAGER);
  }, [normalizedRole]);

  const isClockingOnly = useMemo(() => {
    // Personal scope strictly for pure Clocking role or Admin (per requirement), excluding HR/Chief/Manager
    const isClocking = normalizedRole === 'clocking' || isAdmin;
    return !canSeeCompanyBlocks && !isManager && isClocking;
  }, [normalizedRole, canSeeCompanyBlocks, isManager, isAdmin]);

  const isHr = useMemo(() => normalizedRole === 'hr', [normalizedRole]);

  // Determine scope for context
  const scopeForContext: DashboardScope = canSeeCompanyBlocks
    ? 'company'
    : isManager
      ? 'team'
      : 'personal';

  // Free tier — skip the failing KPI fetches and showcase the platform instead.
  // The TierEnforcementMiddleware blocks dashboard endpoints for Free users; mounting
  // DashboardContent here would just surface a generic "Failed to load" error.
  //
  // Gate on `subscriptionData` being resolved: `isFree` defaults to true while
  // /api/Subscription/me is still in flight (coerceTier(undefined) === "Free"),
  // so showing the showcase before the tier is known would flash FreeTierShowcase
  // at Paid users. Only render it once we affirmatively know the tier is Free;
  // otherwise fall through to the real dashboard (fail-open, matching MasterLayout).
  if (isFree && subscriptionData) {
    return <FreeTierShowcase firstName={firstName} />;
  }

  return (
    <DashboardProvider period={period} scope={scopeForContext} currencyLabel={currency}>
      <DashboardContent
        firstName={firstName}
        isClockingOnly={isClockingOnly}
        isHr={isHr}
        scope={scopeForContext}
        period={period}
        currency={currency}
        currencyOptions={currencyOptions}
        onPeriodChange={setPeriod}
        onCurrencyChange={setCurrency}
      />
    </DashboardProvider>
  );
};

// =============================================================================
// Redux Connection
// =============================================================================

const mapStateToProps = (state: any) => {
  const rate = state.user?.CompanyCurrencyRate;
  const baseCurrency = rate?.baseCurrency;
  return {
    role: state.user.role,
    companyName: state.user.companyName,
    firstName:
      state.user.firstName || state.user.userFirstName || state.user.currentUserFirstName,
    rtl: state.user.rtl,
    isManager: state.user.isManager,
    baseCurrencyCode: baseCurrency?.currencyCode,
    baseCurrencySymbol: baseCurrency?.currencySymbol,
  };
};

export default connect(mapStateToProps)(CompanyDashboard as any);
